import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  Books, UploadSimple, MagnifyingGlass, Trash, X, ArrowLeft, FileText, Tag, User, CaretDown,
  CaretLeft, CaretRight, BookmarkSimple,
} from "@phosphor-icons/react";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import { Header, Footer } from "../components/Layout";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";

// pdfjs worker — must match the pdfjs-dist version that react-pdf bundles.
// Using a CDN URL avoids CRA build-config gymnastics for the .mjs worker.
pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

export default function AdminLibraryPage() {
  const { user, loading } = useAuth();
  const [books, setBooks] = useState([]);
  const [filterAuthor, setFilterAuthor] = useState("");
  const [filterTopic, setFilterTopic] = useState("");
  const [filterQuery, setFilterQuery] = useState("");
  const [showUpload, setShowUpload] = useState(false);
  const [activeBook, setActiveBook] = useState(null);
  const [refreshTick, setRefreshTick] = useState(0);

  useEffect(() => {
    const params = new URLSearchParams();
    if (filterAuthor) params.set("author", filterAuthor);
    if (filterTopic) params.set("topic", filterTopic);
    if (filterQuery) params.set("q", filterQuery);
    api.get(`/super-admin/library?${params.toString()}`)
      .then(({ data }) => setBooks(data.items || []))
      .catch(() => setBooks([]));
  }, [filterAuthor, filterTopic, filterQuery, refreshTick]);

  const grouped = useMemo(() => {
    const out = {};
    for (const b of books) {
      const key = b.author || "Unattributed";
      (out[key] = out[key] || []).push(b);
    }
    return out;
  }, [books]);

  // ---- Auth gating ----
  if (!loading && (!user || !user.is_super_admin)) {
    return (
      <div className="min-h-[100dvh] bg-[#FAF4E8] flex flex-col">
        <Header />
        <main className="flex-1 max-w-xl mx-auto px-4 py-16 text-center">
          <h1 className="font-serif-display text-3xl">Super Admin only</h1>
          <p className="text-muted mt-3">This research library is restricted to admin accounts.</p>
        </main>
        <Footer />
      </div>
    );
  }

  if (activeBook) {
    return <BookReaderView book={activeBook} onClose={() => setActiveBook(null)} />;
  }

  return (
    <div className="min-h-[100dvh] bg-[#FAF4E8] flex flex-col" data-testid="admin-library-page">
      <Header />
      <main className="flex-1 max-w-6xl mx-auto w-full px-4 sm:px-6 py-8">

        <div className="flex items-start justify-between flex-wrap gap-3 mb-6">
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-amber-700 font-semibold">Super Admin · Private Research Library</div>
            <h1 className="font-serif-display text-3xl sm:text-4xl mt-2 font-light flex items-center gap-3">
              <Books size={32} weight="duotone" className="text-amber-600" />
              Astrology Library
            </h1>
            <p className="text-muted text-sm mt-1.5">Your personal PDF reading shelf. Upload, organise, search, read.</p>
          </div>
          <button
            onClick={() => setShowUpload(true)}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-gradient-to-r from-amber-500 via-orange-500 to-rose-500 text-white text-sm font-semibold shadow-lg shadow-orange-200/60 hover:shadow-xl hover:-translate-y-0.5 transition-all"
            data-testid="library-upload-btn"
          >
            <UploadSimple size={16} weight="bold" /> Upload PDF
          </button>
        </div>

        {/* Filters */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-7">
          <div className="relative">
            <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted" />
            <input
              type="text"
              value={filterQuery}
              onChange={(e) => setFilterQuery(e.target.value)}
              placeholder="Search title, author, description…"
              className="w-full pl-9 pr-3 py-2.5 rounded-full border border-soft bg-white text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none"
              data-testid="library-search-input"
            />
          </div>
          <input
            type="text"
            value={filterAuthor}
            onChange={(e) => setFilterAuthor(e.target.value)}
            placeholder="Filter by author"
            className="px-3 py-2.5 rounded-full border border-soft bg-white text-sm focus:border-amber-400 outline-none"
            data-testid="library-author-filter"
          />
          <input
            type="text"
            value={filterTopic}
            onChange={(e) => setFilterTopic(e.target.value)}
            placeholder="Filter by topic"
            className="px-3 py-2.5 rounded-full border border-soft bg-white text-sm focus:border-amber-400 outline-none"
            data-testid="library-topic-filter"
          />
        </div>

        {/* Grouped grid */}
        {books.length === 0 ? (
          <div className="text-center py-16">
            <Books size={48} weight="duotone" className="mx-auto text-muted mb-4" />
            <p className="text-muted">No books yet. Click <strong className="text-amber-700">Upload PDF</strong> to add your first one.</p>
          </div>
        ) : (
          Object.entries(grouped).map(([author, list]) => (
            <section key={author} className="mb-10">
              <div className="flex items-center gap-2 mb-3">
                <User size={16} weight="duotone" className="text-amber-600" />
                <h2 className="font-serif-display text-xl">{author}</h2>
                <span className="text-[10px] text-muted ml-1">{list.length} book{list.length === 1 ? "" : "s"}</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {list.map((b) => (
                  <BookCard
                    key={b.book_id}
                    book={b}
                    onOpen={() => setActiveBook(b)}
                    onDelete={async () => {
                      if (!window.confirm(`Delete "${b.title}"? This cannot be undone.`)) return;
                      try {
                        await api.delete(`/super-admin/library/${b.book_id}`);
                        toast.success("Book deleted");
                        setRefreshTick((t) => t + 1);
                      } catch {
                        toast.error("Could not delete");
                      }
                    }}
                  />
                ))}
              </div>
            </section>
          ))
        )}
      </main>
      <Footer />

      {showUpload && (
        <UploadModal
          onClose={() => setShowUpload(false)}
          onDone={() => { setShowUpload(false); setRefreshTick((t) => t + 1); }}
        />
      )}
    </div>
  );
}

function BookCard({ book, onOpen, onDelete }) {
  const [downloading, setDownloading] = useState(false);

  const handleDownload = async () => {
    if (downloading) return;
    setDownloading(true);
    try {
      const resp = await api.get(`/super-admin/library/${book.book_id}/file`, {
        responseType: "blob",
      });
      const blob = new Blob([resp.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = book.original_filename || `${book.title}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Download failed — please retry");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="group bg-white rounded-xl border border-soft p-4 hover:shadow-lg transition-all" data-testid={`book-card-${book.book_id}`}>
      <div
        onClick={onOpen}
        className="cursor-pointer"
        role="button"
      >
        <div className="flex items-start gap-3">
          <div className="w-12 h-14 rounded bg-gradient-to-br from-amber-500 via-orange-500 to-rose-500 flex items-center justify-center flex-none shadow-md">
            <FileText size={22} weight="duotone" className="text-white" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="font-medium text-[#3F2D1B] text-sm leading-snug line-clamp-2">{book.title}</div>
            {book.topic && (
              <div className="inline-flex items-center gap-1 mt-1 text-[10px] uppercase tracking-wider text-amber-700">
                <Tag size={10} weight="bold" />{book.topic}
              </div>
            )}
            <div className="text-[10px] text-muted mt-1 font-mono tabular-nums">
              {book.pages} pages · {(book.size_bytes / (1024 * 1024)).toFixed(1)} MB
            </div>
          </div>
        </div>
        {book.description && (
          <p className="text-xs text-muted mt-3 leading-relaxed line-clamp-2">{book.description}</p>
        )}
      </div>
      <div className="flex items-center gap-2 mt-3 pt-3 border-t border-soft/60">
        <button onClick={onOpen} className="flex-1 text-xs px-3 py-1.5 rounded-full bg-amber-50 text-amber-700 font-medium hover:bg-amber-100" data-testid={`read-${book.book_id}`}>
          Read
        </button>
        <button
          onClick={handleDownload}
          disabled={downloading}
          className="text-xs px-3 py-1.5 rounded-full bg-stone-100 text-stone-700 font-medium hover:bg-stone-200 disabled:opacity-50"
          data-testid={`download-${book.book_id}`}
        >
          {downloading ? "…" : "PDF"}
        </button>
        <button onClick={onDelete} className="text-xs p-1.5 rounded-full text-rose-600 hover:bg-rose-50" title="Delete" data-testid={`delete-${book.book_id}`}>
          <Trash size={14} />
        </button>
      </div>
    </div>
  );
}

function UploadModal({ onClose, onDone }) {
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState("");
  const [author, setAuthor] = useState("");
  const [topic, setTopic] = useState("");
  const [description, setDescription] = useState("");
  const [busy, setBusy] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file) { toast.error("Please pick a PDF"); return; }
    if (!title.trim()) { toast.error("Title is required"); return; }
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("title", title.trim());
    fd.append("author", author.trim());
    fd.append("topic", topic.trim());
    fd.append("description", description.trim());
    try {
      await api.post("/super-admin/library/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Uploaded — text extracted and indexed");
      onDone();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={handleSubmit}
        className="bg-white rounded-xl w-full max-w-lg p-6 shadow-2xl"
        data-testid="library-upload-modal"
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-serif-display text-xl text-[#3F2D1B]">Upload PDF</h2>
          <button type="button" onClick={onClose} className="p-1 rounded-full hover:bg-black/5"><X size={16} /></button>
        </div>
        <div className="space-y-3">
          <div>
            <label className="text-[10px] uppercase tracking-widest text-muted">PDF file</label>
            <input
              type="file"
              accept="application/pdf"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="mt-1 w-full text-sm file:mr-3 file:py-2 file:px-4 file:rounded-full file:border-0 file:bg-amber-500 file:text-white file:font-medium hover:file:bg-amber-600 cursor-pointer"
              data-testid="library-upload-file"
            />
            {file && <div className="text-xs text-muted mt-1">{file.name} · {(file.size / (1024 * 1024)).toFixed(2)} MB</div>}
          </div>
          <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Title *" className="w-full px-3 py-2 rounded-md border border-soft text-sm focus:border-amber-400 outline-none" data-testid="library-upload-title" />
          <input type="text" value={author} onChange={(e) => setAuthor(e.target.value)} placeholder="Author (e.g. R.G. Rao)" className="w-full px-3 py-2 rounded-md border border-soft text-sm focus:border-amber-400 outline-none" data-testid="library-upload-author" />
          <input type="text" value={topic} onChange={(e) => setTopic(e.target.value)} placeholder="Topic (e.g. Nadi Astrology, KP, Jaimini)" className="w-full px-3 py-2 rounded-md border border-soft text-sm focus:border-amber-400 outline-none" data-testid="library-upload-topic" />
          <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3} placeholder="Short description / notes" className="w-full px-3 py-2 rounded-md border border-soft text-sm focus:border-amber-400 outline-none" data-testid="library-upload-description" />
        </div>
        <button type="submit" disabled={busy} className="w-full mt-5 px-5 py-2.5 rounded-full bg-gradient-to-r from-amber-500 via-orange-500 to-rose-500 text-white text-sm font-semibold disabled:opacity-50" data-testid="library-upload-submit">
          {busy ? "Uploading…" : "Upload & Index"}
        </button>
      </form>
    </div>
  );
}

function BookReaderView({ book, onClose }) {
  const [searchQuery, setSearchQuery] = useState("");
  const [hits, setHits] = useState(null);
  const [searching, setSearching] = useState(false);
  // pdfState: { status: "loading" } | { status: "ready", url } | { status: "error", message }
  const [pdfState, setPdfState] = useState({ status: "loading" });
  const [numPages, setNumPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  // Page we should jump to once the PDF finishes loading (from backend bookmark).
  const [resumePage, setResumePage] = useState(null);
  const [resumeNotice, setResumeNotice] = useState(null); // {page, justSaved?}
  const [bookmarkSaving, setBookmarkSaving] = useState(false);
  const [bookmarkSaved, setBookmarkSaved] = useState(false);

  const scrollContainerRef = useRef(null);
  const pageRefs = useRef({});
  // Track the last page we actually persisted so we don't spam the backend.
  const lastSavedPageRef = useRef(null);
  // Skip the very first page-change event right after we programmatically
  // restored the bookmark — otherwise the observer would re-save page 1
  // before the scroll-to-page lands.
  const programmaticScrollUntilRef = useRef(0);

  // Fetch the PDF via axios (sends session_token cookie automatically), wrap
  // it in a Blob URL, and feed it to react-pdf. This goes through the same
  // authenticated channel as the rest of the page.
  useEffect(() => {
    let cancelled = false;
    let createdUrl = null;
    api.get(`/super-admin/library/${book.book_id}/file`, { responseType: "blob" })
      .then((resp) => {
        if (cancelled) return;
        const blob = new Blob([resp.data], { type: "application/pdf" });
        createdUrl = URL.createObjectURL(blob);
        setPdfState({ status: "ready", url: createdUrl });
      })
      .catch(async (err) => {
        if (cancelled) return;
        // Because the request uses `responseType: blob`, error bodies are
        // also blobs — we have to read+parse them to surface the backend's
        // human-readable `detail` message (e.g. "Please re-upload the PDF").
        let message = "Could not load PDF";
        try {
          const blob = err?.response?.data;
          if (blob && typeof blob.text === "function") {
            const txt = await blob.text();
            try { message = JSON.parse(txt)?.detail || message; }
            catch (_) { /* not JSON — fall through */ }
          } else if (err?.response?.data?.detail) {
            message = err.response.data.detail;
          }
        } catch (_) { /* keep default message */ }
        setPdfState({ status: "error", message });
      });
    return () => {
      cancelled = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [book.book_id]);

  // Fetch the bookmark for this book + this admin in parallel with the PDF.
  useEffect(() => {
    let cancelled = false;
    api.get(`/super-admin/library/${book.book_id}/bookmark`)
      .then(({ data }) => {
        if (cancelled) return;
        const p = Number(data?.page) || 1;
        if (p > 1) {
          setResumePage(p);
          setResumeNotice({ page: p, justSaved: false });
        }
      })
      .catch(() => { /* no bookmark yet — start at page 1 */ });
    return () => { cancelled = true; };
  }, [book.book_id]);

  const pdfBlobUrl = pdfState.status === "ready" ? pdfState.url : null;
  const loadError = pdfState.status === "error" ? pdfState.message : null;

  const onDocumentLoadSuccess = useCallback(({ numPages: total }) => {
    setNumPages(total);
  }, []);

  const onDocumentLoadError = useCallback((err) => {
    setPdfState({ status: "error", message: err?.message || "Could not render PDF" });
  }, []);

  // After numPages is known and we have a resume target, jump to that page.
  useEffect(() => {
    if (!numPages || !resumePage) return;
    // Wait one frame so the Page components have mounted and attached refs.
    const t = setTimeout(() => {
      const target = Math.min(Math.max(1, resumePage), numPages);
      const el = pageRefs.current[target];
      if (el && scrollContainerRef.current) {
        // Mark a quiet window so the observer doesn't immediately re-save 1.
        programmaticScrollUntilRef.current = Date.now() + 1500;
        el.scrollIntoView({ behavior: "auto", block: "start" });
        setCurrentPage(target);
        lastSavedPageRef.current = target;
      }
      setResumePage(null);
    }, 100);
    return () => clearTimeout(t);
  }, [numPages, resumePage]);

  // Keep track of which page is most-visible inside the scroll container.
  // Implementation notes:
  //  - We previously used IntersectionObserver with a custom root, which is
  //    flaky on iOS Safari.
  //  - The naive "use centerY = container.height/2" version was *worse* —
  //    on phones the container is sometimes ~12,000px tall (flex parent not
  //    bounding height), so centerY landed in the middle of the document
  //    and immediately reported "we're on page 13" the moment the reader
  //    opened. That triggered an autosave of page 13, which then resumed
  //    every future open at page 13.
  //  - Fix: use the actual viewport centre (top of container in the
  //    viewport + min(container height, viewport height) / 2), and skip the
  //    very first dispatch (no scroll has happened yet — only react to
  //    real user scrolls).
  //  - Listen on both `window` and the container so we work regardless of
  //    which one actually owns the scrollbar.
  useEffect(() => {
    if (!numPages || !scrollContainerRef.current) return;
    const root = scrollContainerRef.current;
    let raf = null;
    let userHasScrolled = false;
    let lastFound = currentPage;

    const computeVisiblePage = () => {
      raf = null;
      if (!userHasScrolled) return; // ignore the synthetic initial dispatch
      const containerRect = root.getBoundingClientRect();
      // Viewport centre clamped to the visible portion of the container.
      const vh = window.innerHeight || document.documentElement.clientHeight || 800;
      const visibleTop = Math.max(0, containerRect.top);
      const visibleBottom = Math.min(vh, containerRect.bottom);
      const centerY = (visibleTop + visibleBottom) / 2;
      // Find the page whose visible area is largest at centerY.
      let best = lastFound;
      let bestArea = -1;
      for (let p = 1; p <= numPages; p++) {
        const el = pageRefs.current[p];
        if (!el) continue;
        const r = el.getBoundingClientRect();
        // Pages fully above the viewport — skip.
        if (r.bottom < visibleTop - 200) continue;
        // Pages fully below — stop scanning, they're sequential.
        if (r.top > visibleBottom + 200) break;
        const overlap = Math.max(0, Math.min(r.bottom, visibleBottom) - Math.max(r.top, visibleTop));
        // Prefer the page that overlaps the viewport centre.
        const containsCentre = r.top <= centerY && r.bottom >= centerY;
        const score = containsCentre ? overlap + 100000 : overlap;
        if (score > bestArea) { bestArea = score; best = p; }
      }
      if (best !== lastFound) {
        lastFound = best;
        setCurrentPage(best);
      }
    };

    const onScroll = () => {
      userHasScrolled = true;
      if (raf == null) raf = requestAnimationFrame(computeVisiblePage);
    };
    // Both window-level and container-level scroll — whichever fires we'll
    // recompute. iOS Safari sometimes routes scroll to window even when a
    // child has `overflow:auto` because of momentum-scrolling quirks.
    window.addEventListener("scroll", onScroll, { passive: true });
    root.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      root.removeEventListener("scroll", onScroll);
      if (raf != null) cancelAnimationFrame(raf);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [numPages]);

  // Persist bookmark whenever the visible page settles on a new value.
  // Debounced so quick scrolling doesn't spam the backend.
  useEffect(() => {
    if (!numPages) return;
    if (Date.now() < programmaticScrollUntilRef.current) return;
    if (currentPage === lastSavedPageRef.current) return;
    const handle = setTimeout(() => {
      if (currentPage === lastSavedPageRef.current) return;
      setBookmarkSaving(true);
      api.post(`/super-admin/library/${book.book_id}/bookmark`, { page: currentPage })
        .then(() => {
          lastSavedPageRef.current = currentPage;
          setBookmarkSaved(true);
          setTimeout(() => setBookmarkSaved(false), 1500);
        })
        .catch(() => { /* swallow — we'll retry on the next page settle */ })
        .finally(() => setBookmarkSaving(false));
    }, 600);
    return () => clearTimeout(handle);
  }, [currentPage, numPages, book.book_id]);

  const jumpToPage = (p) => {
    const target = Math.min(Math.max(1, p), numPages || 1);
    const el = pageRefs.current[target];
    if (el && scrollContainerRef.current) {
      programmaticScrollUntilRef.current = Date.now() + 800;
      el.scrollIntoView({ behavior: "smooth", block: "start" });
      setCurrentPage(target);
    }
  };

  const runSearch = async (e) => {
    e?.preventDefault();
    if (searchQuery.trim().length < 2) return;
    setSearching(true);
    try {
      const { data } = await api.get(`/super-admin/library/${book.book_id}/search`, { params: { q: searchQuery.trim() } });
      setHits(data);
    } catch {
      toast.error("Search failed");
    } finally {
      setSearching(false);
    }
  };

  const handleDownload = () => {
    if (!pdfBlobUrl) return;
    const a = document.createElement("a");
    a.href = pdfBlobUrl;
    a.download = book.original_filename || `${book.title}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  // Memoize file prop so react-pdf doesn't re-fetch on every render.
  const pdfFile = useMemo(() => (pdfBlobUrl ? { url: pdfBlobUrl } : null), [pdfBlobUrl]);

  return (
    <div className="h-[100dvh] bg-[#1a1410] flex flex-col overflow-hidden" data-testid="book-reader">
      <header className="bg-[#2a1f17] text-amber-50 px-4 py-3 flex items-center gap-3 border-b border-amber-900/50">
        <button onClick={onClose} className="p-1.5 rounded hover:bg-white/10" data-testid="reader-back">
          <ArrowLeft size={18} />
        </button>
        <div className="flex-1 min-w-0">
          <div className="font-medium truncate text-sm">{book.title}</div>
          <div className="text-[11px] opacity-70">
            {book.author || "Unattributed"} · {numPages ? `${numPages} pages` : `${book.pages} pages`}
            {numPages > 0 && (
              <>
                {" · "}
                <span className="tabular-nums">page {currentPage} / {numPages}</span>
                {bookmarkSaving && <span className="ml-1 text-amber-300">· saving…</span>}
                {bookmarkSaved && !bookmarkSaving && <span className="ml-1 text-emerald-300">· saved</span>}
              </>
            )}
          </div>
        </div>

        {/* Page nav (compact) */}
        {numPages > 0 && (
          <div className="hidden sm:flex items-center gap-1.5">
            <button
              onClick={() => jumpToPage(currentPage - 1)}
              disabled={currentPage <= 1}
              className="p-1.5 rounded hover:bg-white/10 disabled:opacity-30"
              aria-label="Previous page"
              data-testid="reader-prev-page"
            >
              <CaretLeft size={14} />
            </button>
            <input
              type="number"
              min={1}
              max={numPages}
              value={currentPage}
              onChange={(e) => {
                const v = Math.max(1, Math.min(numPages, Number(e.target.value) || 1));
                setCurrentPage(v);
              }}
              onBlur={(e) => jumpToPage(Number(e.target.value) || 1)}
              onKeyDown={(e) => { if (e.key === "Enter") jumpToPage(Number(e.currentTarget.value) || 1); }}
              className="w-14 text-center bg-white/10 text-amber-50 text-xs py-1 rounded-md outline-none focus:bg-white/20 tabular-nums"
              data-testid="reader-page-input"
            />
            <button
              onClick={() => jumpToPage(currentPage + 1)}
              disabled={currentPage >= numPages}
              className="p-1.5 rounded hover:bg-white/10 disabled:opacity-30"
              aria-label="Next page"
              data-testid="reader-next-page"
            >
              <CaretRight size={14} />
            </button>
          </div>
        )}

        <form onSubmit={runSearch} className="hidden md:flex items-center gap-2">
          <div className="relative">
            <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-amber-200/70" />
            <input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search within book…"
              className="pl-8 pr-3 py-1.5 rounded-full bg-white/10 text-amber-50 placeholder:text-amber-200/40 text-sm outline-none focus:bg-white/20 w-48"
              data-testid="reader-search-input"
            />
          </div>
          <button type="submit" disabled={searching} className="text-xs px-3 py-1.5 rounded-full bg-amber-500 text-[#1a1410] font-semibold hover:bg-amber-400 disabled:opacity-50">
            {searching ? "…" : "Search"}
          </button>
        </form>

        <button
          onClick={handleDownload}
          disabled={!pdfBlobUrl}
          className="text-xs px-3 py-1.5 rounded-full bg-amber-500 text-[#1a1410] font-semibold hover:bg-amber-400 disabled:opacity-50"
          data-testid="reader-download"
        >
          Download
        </button>
      </header>

      {/* Resume toast inside the dark chrome — auto-dismisses after 4s */}
      {resumeNotice && (
        <ResumeBanner page={resumeNotice.page} onDismiss={() => setResumeNotice(null)} />
      )}

      <div className="flex-1 flex min-h-0">
        {hits && (
          <aside className="w-72 bg-[#2a1f17] text-amber-50 border-r border-amber-900/50 overflow-y-auto p-3" data-testid="reader-search-results">
            <div className="flex items-center justify-between mb-3">
              <div className="text-[10px] uppercase tracking-widest text-amber-300">
                {hits.total} hit{hits.total === 1 ? "" : "s"} for &quot;{hits.q}&quot;
              </div>
              <button onClick={() => setHits(null)} className="text-amber-300 hover:text-amber-100"><X size={12} /></button>
            </div>
            {hits.hits.length === 0 ? (
              <p className="text-xs text-amber-200/70">No matches found.</p>
            ) : (
              <ul className="space-y-2">
                {hits.hits.map((h, i) => (
                  <li key={i} className="text-xs leading-relaxed bg-white/5 rounded p-2 border border-amber-900/40">
                    <HighlightedSnippet text={h.snippet} q={hits.q} />
                  </li>
                ))}
              </ul>
            )}
          </aside>
        )}

        <main className="flex-1 bg-[#1a1410] relative min-h-0 min-w-0">
          {loadError ? (
            <div className="absolute inset-0 flex items-center justify-center text-center text-amber-200/80 px-6">
              <div>
                <div className="font-serif-display text-xl mb-2">Couldn&apos;t load this PDF</div>
                <div className="text-sm opacity-80">{loadError}</div>
                <button onClick={onClose} className="mt-4 text-xs px-4 py-2 rounded-full bg-amber-500 text-[#1a1410] font-semibold hover:bg-amber-400">
                  Back to library
                </button>
              </div>
            </div>
          ) : !pdfBlobUrl ? (
            <div className="absolute inset-0 flex items-center justify-center text-amber-200/70 text-sm animate-pulse">
              Loading PDF…
            </div>
          ) : (
            <div
              ref={scrollContainerRef}
              className="h-full overflow-y-auto py-6 px-2 sm:px-6"
              data-testid="reader-scroll-container"
            >
              <Document
                file={pdfFile}
                onLoadSuccess={onDocumentLoadSuccess}
                onLoadError={onDocumentLoadError}
                loading={<div className="text-center text-amber-200/70 text-sm py-12">Rendering PDF…</div>}
                error={<div className="text-center text-rose-300 text-sm py-12">Could not render this PDF.</div>}
                className="flex flex-col items-center gap-4"
              >
                {Array.from({ length: numPages }, (_, i) => i + 1).map((p) => {
                  // ── Windowed rendering ──────────────────────────────
                  // Mounting EVERY page at once OOM-crashes iPhone Safari
                  // (~300 MB per-tab budget). We only render the actual
                  // canvas for pages near the viewport; the rest are
                  // lightweight placeholder divs that still hold their
                  // height so the IntersectionObserver + scroll position
                  // continue working correctly.
                  const inWindow = Math.abs(p - currentPage) <= 3;
                  const pageWidth = Math.min(
                    (typeof window !== "undefined" ? window.innerWidth : 900) - 24,
                    900,
                  );
                  // Approximate A4 ratio for placeholders — close enough
                  // for scroll-anchoring; real pages snap to correct size
                  // once they enter the window.
                  const placeholderHeight = Math.round(pageWidth * 1.35);
                  return (
                    <div
                      key={p}
                      ref={(el) => { if (el) pageRefs.current[p] = el; }}
                      data-page-number={p}
                      className="bg-white shadow-xl rounded-sm overflow-hidden"
                      style={!inWindow ? { width: pageWidth, height: placeholderHeight } : undefined}
                    >
                      {inWindow ? (
                        <Page
                          pageNumber={p}
                          width={pageWidth}
                          // Cap DPR at 1.5 — iPhone retina runs at 3x
                          // which gives gorgeous canvases that blow the
                          // memory budget. 1.5 looks indistinguishable on
                          // a phone screen at this width.
                          devicePixelRatio={typeof window !== "undefined" && window.devicePixelRatio > 1.5 ? 1.5 : (window?.devicePixelRatio || 1)}
                          renderTextLayer={false}
                          renderAnnotationLayer={false}
                          loading={<div style={{ width: pageWidth, height: placeholderHeight }} className="flex items-center justify-center text-stone-400 text-xs">Page {p}…</div>}
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-stone-300 text-xs select-none">
                          Page {p}
                        </div>
                      )}
                    </div>
                  );
                })}
              </Document>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}

function ResumeBanner({ page, onDismiss }) {
  useEffect(() => {
    const t = setTimeout(onDismiss, 4500);
    return () => clearTimeout(t);
  }, [onDismiss]);
  return (
    <div className="bg-amber-500/10 border-b border-amber-500/30 text-amber-100 px-4 py-2 text-xs flex items-center gap-2" data-testid="reader-resume-banner">
      <BookmarkSimple size={14} weight="fill" className="text-amber-300" />
      <span>Resumed from where you left off — <span className="font-semibold tabular-nums">page {page}</span>.</span>
      <button onClick={onDismiss} className="ml-auto text-amber-300 hover:text-amber-100" aria-label="Dismiss">
        <X size={12} />
      </button>
    </div>
  );
}

function HighlightedSnippet({ text, q }) {
  if (!q) return <span>{text}</span>;
  const parts = text.split(new RegExp(`(${q.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`, "ig"));
  return (
    <span>
      {parts.map((p, i) =>
        p.toLowerCase() === q.toLowerCase()
          ? <mark key={i} className="bg-amber-400/40 text-amber-100 rounded px-0.5">{p}</mark>
          : <span key={i}>{p}</span>
      )}
    </span>
  );
}
